import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { registerNewAccount, clearRegistrationError, resetRegistrationState } from "../../redux/slices/usersSlice";
import { googleOAuthLogin, logout, clearError } from "../../redux/slices/authSlice";
import {
    IconArrowRight,
    IconBrandGoogle,
    IconBrandFacebook,
    IconBrandLinkedin,
    IconBrandInstagram,
    IconBrandYoutube,
    IconBrandTwitter,
    IconUpload,
    IconX
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import LoginImage from "../../assets/images/login.jpg";
import { NavLink } from "react-router-dom";

export default function SignUp() {
    const [formData, setFormData] = useState({
        full_name: "",
        email: "",
        phone_number: "",
        profile_picture: null
    });
    const [previewImage, setPreviewImage] = useState(null);

    // Google OAuth states
    const [googleLoading, setGoogleLoading] = useState(false);
    const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
    const [googleError, setGoogleError] = useState(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get registration states from Redux
    const {
        registrationLoading,
        registrationError,
        registrationSuccess
    } = useSelector((state) => state.users);

    // Clear errors and reset state when component mounts
    useEffect(() => {
        dispatch(resetRegistrationState());
        dispatch(clearError()); // Clear auth errors too
    }, [dispatch]);

    // Clear errors when user starts typing
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear errors when user starts typing
        if (registrationError) {
            dispatch(clearRegistrationError());
        }
        if (googleError) {
            setGoogleError(null);
        }
    };

    // Helper function to get error message (same as login)
    const getErrorMessage = (error) => {
        if (!error) return null;

        if (typeof error === 'string') {
            return error;
        }

        if (error.message) {
            return error.message;
        }

        if (error.detail) {
            return error.detail;
        }

        if (error.non_field_errors && Array.isArray(error.non_field_errors)) {
            return error.non_field_errors[0];
        }

        // Handle field-specific errors for registration
        if (error.email && Array.isArray(error.email)) {
            return `Email: ${error.email[0]}`;
        }

        if (error.phone_number && Array.isArray(error.phone_number)) {
            return `Phone: ${error.phone_number[0]}`;
        }

        if (error.full_name && Array.isArray(error.full_name)) {
            return `Name: ${error.full_name[0]}`;
        }

        // Handle multiple field errors
        const fieldErrors = [];
        ['full_name', 'email', 'phone_number', 'profile_picture'].forEach(field => {
            if (error[field] && Array.isArray(error[field])) {
                fieldErrors.push(`${field.replace('_', ' ')}: ${error[field][0]}`);
            }
        });

        if (fieldErrors.length > 0) {
            return fieldErrors[0]; // Show first error
        }

        return 'Registration failed. Please check your information and try again.';
    };

    // Load Google Identity Services (same as before)
    useEffect(() => {
        const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
        if (existingScript) {
            existingScript.remove();
        }

        if (window.google) {
            setIsGoogleLoaded(true);
            initializeGoogleButton();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        script.onload = () => {
            setTimeout(() => {
                if (window.google && window.google.accounts) {
                    setIsGoogleLoaded(true);
                    initializeGoogleButton();
                } else {
                    setGoogleError('Failed to initialize Google services');
                }
            }, 500);
        };

        script.onerror = (error) => {
            setGoogleError('Failed to load Google services');
        };

        document.head.appendChild(script);

        return () => {
            const scriptToRemove = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, []);

    const initializeGoogleButton = () => {
        if (!window.google || !window.google.accounts) {
            return;
        }

        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

        try {
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: handleGoogleResponse,
                auto_select: false,
                cancel_on_tap_outside: false,
            });

            const container = document.getElementById('google-signup-button-container');
            if (container) {
                window.google.accounts.id.renderButton(container, {
                    theme: 'outline',
                    size: 'large',
                    text: 'signup_with',
                    shape: 'rectangular',
                });
            }
        } catch (error) {
            setGoogleError('Failed to initialize Google button');
        }
    };

    const handleGoogleResponse = async (response) => {
        if (!response || !response.credential) {
            setGoogleLoading(false);
            setGoogleError('No credential received from Google');
            return;
        }

        setGoogleLoading(true);
        setGoogleError(null);

        try {
            const resultAction = await dispatch(googleOAuthLogin(response.credential));

            if (googleOAuthLogin.fulfilled.match(resultAction)) {
                setGoogleLoading(false);
                setGoogleError(null);

                const userRole = resultAction.payload.user_detail.role || resultAction.payload.user_detail.user_account_type;

                if (["super_admin", "manager", "editor"].includes(userRole)) {
                    navigate("/admin");
                } else if (userRole === "customer") {
                    navigate("/");
                } else {
                    dispatch(logout());
                }
            } else {
                setGoogleLoading(false);
                setGoogleError(resultAction.payload?.message || 'Google signup failed');
            }
        } catch (error) {
            setGoogleLoading(false);
            setGoogleError('Google authentication failed: ' + error.message);
        }
    };

    const handleDirectGoogleSignup = async () => {
        if (!window.google || !window.google.accounts) {
            setGoogleError('Google services not loaded');
            return;
        }

        setGoogleLoading(true);
        setGoogleError(null);

        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

        try {
            window.google.accounts.id.initialize({
                client_id: clientId,
                callback: handleGoogleResponse,
                auto_select: false,
                cancel_on_tap_outside: false,
                use_fedcm_for_prompt: false,
            });

            const tempDiv = document.createElement('div');
            document.body.appendChild(tempDiv);

            window.google.accounts.id.renderButton(tempDiv, {
                theme: 'outline',
                size: 'large',
                text: 'signup_with',
                shape: 'rectangular',
                width: '300',
            });

            setTimeout(() => {
                const googleButton = tempDiv.querySelector('div[role="button"]');
                if (googleButton) {
                    googleButton.click();
                } else {
                    window.google.accounts.id.prompt((notification) => {
                        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                            setGoogleLoading(false);
                            setGoogleError('Google sign-up not available. Please try again.');
                        }
                    });
                }

                setTimeout(() => {
                    if (document.body.contains(tempDiv)) {
                        document.body.removeChild(tempDiv);
                    }
                }, 1000);
            }, 500);
        } catch (error) {
            setGoogleLoading(false);
            setGoogleError('Failed to initialize Google signup');
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                dispatch(clearRegistrationError());
                setGoogleError('File size must be less than 2MB');
                return;
            }

            // Validate file type
            if (!file.type.startsWith('image/')) {
                dispatch(clearRegistrationError());
                setGoogleError('Please select a valid image file');
                return;
            }

            setFormData(prev => ({
                ...prev,
                profile_picture: file
            }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);

            // Clear any previous errors
            if (googleError) setGoogleError(null);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            profile_picture: null
        }));
        setPreviewImage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(clearRegistrationError()); // Clear previous errors

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('full_name', formData.full_name.trim());
            formDataToSend.append('email', formData.email.trim().toLowerCase());
            formDataToSend.append('phone_number', formData.phone_number.trim());
            formDataToSend.append('user_account_type', 'customer');

            if (formData.profile_picture) {
                formDataToSend.append('profile_picture', formData.profile_picture);
            }

            await dispatch(registerNewAccount(formDataToSend)).unwrap();
            // Success is handled by registrationSuccess state
        } catch (err) {
            // Error is handled by Redux rejected case
            console.error("Registration error:", err);
        }
    };

    const isAnyLoading = registrationLoading || googleLoading;

    // Success screen
    if (registrationSuccess) {
        return (
            <div className="min-h-screen flex flex-col bg-[#F9F9FF]">
                <header className="h-16 bg-[#0B0C52] flex items-center justify-between px-4 md:px-8 text-white">
                    <div className="flex flex-row items-center justify-center gap-3">
                        <Link to="/" className="w-[50px] h-[50px] rounded-full bg-white overflow-hidden flex items-center justify-center">
                            <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
                        </Link>
                        <span className="text-md font-slim tracking-wide">Paqtory</span>
                    </div>

                </header>

                <main className="flex-1 flex items-center justify-center px-4 py-8">
                    <div className="max-w-md w-full bg-white rounded-md border border-[#FFD500]/30 p-8 text-center" style={{ boxShadow: "0 0 0 2px #FFD500" }}>
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-4 text-[#0B0C52]">Registration Successful!</h2>
                        <p className="text-gray-600 mb-6">
                            Please check your email to complete your account setup. We've sent a verification link to <span className="font-semibold">{formData.email}</span>.
                        </p>
                        <button
                            onClick={() => navigate("/login")}
                            className="bg-[#0B0C52] text-white px-6 py-2 rounded text-sm font-semibold hover:opacity-90"
                        >
                            Back to Login
                        </button>
                    </div>
                </main>

                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-[#F9F9FF]">
            <header className="h-16 bg-[#0B0C52] flex items-center justify-between px-4 md:px-8 text-white">
                <div className="flex flex-row items-center justify-center gap-3">
                    <Link to="/" className="w-[50px] h-[50px] rounded-full bg-white overflow-hidden flex items-center justify-center">
                        <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
                    </Link>
                    <span className="text-md font-slim tracking-wide">Paqtory</span>
                </div>

            </header>

            <main className="flex-1 flex items-center justify-center px-4 py-8">
                <div className="max-w-5xl w-full flex flex-col lg:flex-row items-center gap-8">
                    <div className="flex justify-center lg:w-1/2">
                        <img src={LoginImage} alt="Sign Up" className="max-w-sm w-full h-auto object-cover rounded-md shadow" />
                    </div>

                    <div className="lg:w-1/2 flex justify-center">
                        <form onSubmit={handleSubmit} className="max-w-sm w-full bg-white rounded-md border border-[#FFD500]/30 p-6" style={{ boxShadow: "0 0 0 2px #FFD500" }}>
                            <h2 className="text-2xl font-bold mb-6 text-[#0B0C52]">Create Your Account</h2>

                            {/* Registration Error Display */}
                            {registrationError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start justify-between">
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-red-700 text-sm">{getErrorMessage(registrationError)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => dispatch(clearRegistrationError())}
                                        className="text-red-500 hover:text-red-700 ml-2"
                                    >
                                        <IconX size={16} />
                                    </button>
                                </div>
                            )}

                            {/* Google Error Display */}
                            {googleError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start justify-between">
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-red-700 text-sm">{googleError}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setGoogleError(null)}
                                        className="text-red-500 hover:text-red-700 ml-2"
                                    >
                                        <IconX size={16} />
                                    </button>
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-sm font-semibold mb-1">Full Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="full_name"
                                    placeholder="Enter your full name"
                                    value={formData.full_name}
                                    onChange={handleInputChange}
                                    className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0C52] ${isAnyLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                    required
                                    disabled={isAnyLoading}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-semibold mb-1">Email <span className="text-red-500">*</span></label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0C52] ${isAnyLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                    required
                                    disabled={isAnyLoading}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-semibold mb-1">Phone Number <span className="text-red-500">*</span></label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    placeholder="Enter your phone number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    className={`w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0C52] ${isAnyLoading ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                    required
                                    disabled={isAnyLoading}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-semibold mb-1">Profile Picture (Optional)</label>
                                <div className="flex items-center gap-4">
                                    <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer ${previewImage ? 'w-24 h-24' : 'w-full py-8'} bg-gray-50 hover:bg-gray-100 transition ${isAnyLoading ? 'pointer-events-none opacity-50' : ''}`}>
                                        {previewImage ? (
                                            <div className="relative w-full h-full">
                                                <img
                                                    src={previewImage}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover rounded-md"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={removeImage}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                    disabled={isAnyLoading}
                                                >
                                                    <IconX size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex flex-col items-center justify-center">
                                                    <IconUpload size={24} className="mb-2 text-gray-500" />
                                                    <p className="text-xs text-gray-500">Upload photo</p>
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    className="hidden"
                                                    disabled={isAnyLoading}
                                                />
                                            </>
                                        )}
                                    </label>
                                    {!previewImage && (
                                        <p className="text-xs text-gray-500">
                                            JPG, PNG up to 2MB
                                        </p>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="bg-[#FFD500] text-[#0B0C52] w-full py-2 rounded font-semibold flex items-center justify-center gap-2 hover:opacity-90 mb-4 disabled:opacity-70 disabled:cursor-not-allowed"
                                disabled={isAnyLoading}
                            >
                                {registrationLoading ? (
                                    <span className="flex items-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#0B0C52]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Registering...
                                    </span>
                                ) : (
                                    <>
                                        Create Account <IconArrowRight size={20} />
                                    </>
                                )}
                            </button>

                            <div className="flex items-center justify-center mb-4">
                                <div className="border-b border-gray-300 flex-1" />
                                <span className="px-2 text-sm text-gray-500">Or</span>
                                <div className="border-b border-gray-300 flex-1" />
                            </div>

                            <div className="mb-4">
                                <button
                                    type="button"
                                    onClick={handleDirectGoogleSignup}
                                    className="w-full p-3 border border-[#0B0C52] rounded hover:bg-[#0B0C52] hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-white"
                                    disabled={isAnyLoading || !isGoogleLoaded}
                                >
                                    {googleLoading ? (
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        <>
                                            <IconBrandGoogle size={20} />
                                            <span className="text-sm font-medium">Sign up with Google</span>
                                        </>
                                    )}
                                </button>

                                <div id="google-signup-button-container" style={{ position: 'absolute', left: '-9999px' }}></div>
                            </div>

                            <div className="text-center">
                                <p className="text-sm mb-2">Already Have An Account?</p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/login")}
                                    className="bg-[#0B0C52] text-white px-6 py-2 rounded text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isAnyLoading}
                                >
                                    Sign In
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

function Footer() {
    return (
        <footer className="bg-[#0B0C52] border-t border-gray-200 py-6">
            <div className="max-w-5xl mx-auto px-4 flex flex-col items-center space-y-3 text-white">
                <div className="flex flex-wrap justify-center space-x-4 text-sm">
                    <NavLink to="/" className="hover:underline">Home</NavLink>
                    <span>|</span>
                    <NavLink to="/product-categories" className="hover:underline">Categories</NavLink>
                    <span>|</span>
                    <NavLink to="/faq" className="hover:underline">FAQ</NavLink>
                    <span>|</span>
                    <NavLink to="/aboutus" className="hover:underline">About Us</NavLink>
                    <span>|</span>
                    <NavLink to="/contactus" className="hover:underline">Contact Us</NavLink>
                </div>
                <div className="flex space-x-3">
                    {/* <SocialIcon href="#facebook">
                        <IconBrandFacebook size={18} />
                    </SocialIcon>
                    <SocialIcon href="#twitter">
                        <IconBrandTwitter size={18} />
                    </SocialIcon>
                    <SocialIcon href="#linkedin">
                        <IconBrandLinkedin size={18} />
                    </SocialIcon> */}
                    <SocialIcon href="https://www.instagram.com/paqtory?igsh=MWQ5MTlsOHhicDg5cQ==">
                        <IconBrandInstagram size={18} />
                    </SocialIcon>
                    {/* <SocialIcon href="#youtube">
                        <IconBrandYoutube size={18} />
                    </SocialIcon> */}
                </div>
                <p className="text-sm text-gray-400">© 2025 paqtory. All Rights Reserved.</p>
            </div>
        </footer>
    );
}

function SocialIcon({ href, children }) {
    return (
        <a
            href={href}
            className="w-8 h-8 bg-white border border-[#0B0C52] rounded flex items-center justify-center text-[#0B0C52] hover:bg-[#0B0C52] hover:text-white transition"
        >
            {children}
        </a>
    );
}